import { Injectable, inject } from '@angular/core';
import { AvailabilityRepository } from '../availability/availability-repository';
import type { CategoryNodeMap } from '../category/category-node-map';
import { computeAsp, sumValue, sumVolume } from '../measure/volume-value-asp';
import type { DecodeSuccess, Observation, SeriesObservation } from '../observation/observation';
import type {
  BrandCategoryMeta,
  CacheBatchResult,
  CacheRowResult,
} from '../observation/observation-repository';
import { ObservationRepository } from '../observation/observation-repository';
import { monthsForDayRange } from '../observation/read-months';
import { RegionalSalesRepository } from '../regional-sales/regional-sales-repository';
import { SegmentEvaluatorService } from '../segment/segment-evaluator.service';
import type { SkuPredicate } from '../segment/sku-predicate';
import type { EAN } from '../shared/ean';
import { type NonEmpty, nonEmptyOf } from '../shared/non-empty';
import type { PartitionedAvailabilityResult } from './availability-route';
import { distributionSlots, evaluateDistribution } from './availability-route';
import type { MeasureId } from './measure-id';
import type { Measurement, MeasurementFilter, MeasurementWindow, ShareMode } from './measurement';
import { filterObservations } from './observation-filter';
import type { PartitionedRegionResult, PartitionedVendorResult } from './region-route';
import {
  evaluateRegionVolume,
  evaluateVendorVolume,
  regionSlots,
  vendorSlots,
} from './region-route';
import { assignObsToBuckets, bucketKeys, observationSourceRange } from './window-mechanics';

// ─── Output types ─────────────────────────────────────────────────────────────

/**
 * Maps time-bucket key to the measure value for that bucket.
 * Key format depends on the window mode:
 *   monthly   → 'YYYY-MM'
 *   rolling28 → 'YYYY-MM-DD' (one per display day)
 *   total     → 'total'
 */
export type BucketMap = ReadonlyMap<string, number | null>;

export type ScalarResult = {
  readonly kind: 'scalar';
  readonly value: number | null;
};

export type SeriesResult = {
  readonly kind: 'series';
  readonly buckets: BucketMap;
};

export type PartitionedResult = {
  readonly kind: 'partitioned';
  /** Maps partition key (EAN for sku, brand-slug for brand) → time-bucket map. */
  readonly partitions: ReadonlyMap<string, BucketMap>;
};

/** Per-partition pre-filtered observations returned by evaluate() for grain:'observations' requests. */
export type PartitionedObservationsResult = {
  readonly kind: 'partitioned-observations';
  /** Maps EAN → vendor-filtered SeriesObservation array. */
  readonly partitions: ReadonlyMap<string, readonly SeriesObservation[]>;
};

export type EvaluationResult =
  | ScalarResult
  | SeriesResult
  | PartitionedResult
  | PartitionedObservationsResult
  | PartitionedAvailabilityResult
  | PartitionedRegionResult
  | PartitionedVendorResult;

// The cast is guarded by the kind check above it: TS cannot narrow a union member
// through a generic discriminant comparison, so the check has to stand in for it.
function narrowResult<K extends EvaluationResult['kind']>(
  result: EvaluationResult,
  kind: K
): Extract<EvaluationResult, { kind: K }> {
  if (result.kind !== kind) {
    throw new Error(`evaluate() returned unexpected kind '${result.kind}'; expected '${kind}'`);
  }
  return result as Extract<EvaluationResult, { kind: K }>;
}

/** Narrows a result at call sites that configure grain:'observations' with shareMode 'absolute'. */
export function asPartitionedObservations(result: EvaluationResult): PartitionedObservationsResult {
  return narrowResult(result, 'partitioned-observations');
}

/** Narrows a result at call sites that configure grain:'observations' with shareMode !== 'absolute'. */
export function asPartitioned(result: EvaluationResult): PartitionedResult {
  return narrowResult(result, 'partitioned');
}

/** Narrows a result at call sites that configure measure:'distribution'. */
export function asPartitionedAvailability(result: EvaluationResult): PartitionedAvailabilityResult {
  return narrowResult(result, 'partitioned-availability');
}

/** Narrows a result at call sites that configure partitionBy:'region'. */
export function asPartitionedRegion(result: EvaluationResult): PartitionedRegionResult {
  return narrowResult(result, 'partitioned-region');
}

/** Narrows a result at call sites that configure partitionBy:'vendor'. */
export function asPartitionedVendor(result: EvaluationResult): PartitionedVendorResult {
  return narrowResult(result, 'partitioned-vendor');
}

// What a caller hands evaluate() beside the measurement.
type EvaluateOptions = {
  readonly signal?: AbortSignal;
  // The bc- metadata the caller holds, grouped by brand slug.
  readonly categoryMeta?: ReadonlyMap<string, readonly BrandCategoryMeta[]>;
};

// ─── Evaluator service ────────────────────────────────────────────────────────

/**
 * Evaluates a valid Measurement query object into per-partition, per-bucket
 * measure values by composing:
 *  - SegmentEvaluatorService  (universe → EAN set)
 *  - ObservationRepository    (facts, including brand-grain readBrandSeries path)
 *  - AvailabilityRepository   (the distribution family's weekly facts)
 *  - window-mechanics helpers  (bucketing)
 *  - measure catalog functions (all arithmetic)
 *
 * Consumed-only (D-03): implements exactly the slot combinations the four in-scope
 * surfaces exercise. Every other combination is rejected loudly with an actionable
 * error naming the unsupported slots — never a silent wrong answer.
 *
 * ## Consumed slot inventory — the observation fact families
 *
 * partitionBy:  sku | brand | none
 * window.mode:  monthly | rolling28 | total
 * shareMode:    absolute | ofUniverse | ofSelection | ofTotal (non-absolute only with brand+observations)
 * filter:       vendors (vendor-key strings from seriesKey()), channels (Channel values — no wire suffixes)
 * measures:     volume | value | asp
 *
 * brand + grain:observations paths:
 *   shareMode=absolute   → evaluateBrandObservations (returns PartitionedObservationsResult)
 *   shareMode≠absolute   → evaluateBrandShares (returns PartitionedResult with share fractions)
 *
 * ## Consumed slot inventory — the distribution (availability) fact family
 *
 * One combination, owned by availability-route.ts (distributionSlots):
 *   sku + observations + window.mode weekly + absolute + no filter + inline brand universe
 *     → evaluateDistribution (returns PartitionedAvailabilityResult)
 *
 * ## Consumed slot inventory — the map (regional sales) fact family
 *
 * Two combinations over one warmed row, owned by region-route.ts:
 *   region + volume + window.mode total + absolute + aggregated + inline universe +
 *   a region grain, optionally narrowed by filter.vendors            (regionSlots)
 *     → evaluateRegionVolume (returns PartitionedRegionResult)
 *   the same slots cut per chain, additionally narrowable after the read by
 *   filter.regions                                                   (vendorSlots)
 *     → evaluateVendorVolume (returns PartitionedVendorResult)
 */
@Injectable()
export class MeasurementEvaluatorService {
  private readonly segmentEvaluator = inject(SegmentEvaluatorService);
  private readonly observationRepo = inject(ObservationRepository);
  private readonly availabilityRepo = inject(AvailabilityRepository);
  private readonly regionalRepo = inject(RegionalSalesRepository);

  /**
   * Evaluates the measurement and returns one result per partition bucket.
   *
   * nodeMap is required wherever the universe has to be resolved to EANs through
   * SegmentEvaluatorService: the sku, none and region partition paths, and
   * measure:distribution when its brand is narrowed by a predicate. The brand path
   * resolves no EANs and may pass null.
   *
   * An aborted `signal` rejects the brand paths' reads; the other routes do not take it.
   * `categoryMeta` is the bc- metadata the caller already holds; the brand + category path reads
   * the metadata of every brand it does not name, and of no other.
   */
  async evaluate(
    measurement: Measurement,
    nodeMap: CategoryNodeMap | null,
    options: EvaluateOptions = {}
  ): Promise<EvaluationResult> {
    assertSupportedSlots(measurement);
    const { signal } = options;

    const { partitionBy } = measurement;

    // The measure picks the fact family before the partition does: distribution reads
    // availability facts, every other measure reads the observation series.
    if (measurement.measure === 'distribution') {
      return evaluateDistribution(
        this.availabilityRepo,
        this.segmentEvaluator,
        measurement,
        nodeMap
      );
    }
    if (partitionBy === 'region') {
      return evaluateRegionVolume(this.regionalRepo, this.segmentEvaluator, measurement, nodeMap);
    }
    if (partitionBy === 'vendor') {
      return evaluateVendorVolume(this.regionalRepo, this.segmentEvaluator, measurement, nodeMap);
    }
    if (partitionBy === 'none') {
      return this.evaluateNone(measurement, nodeMap);
    }
    if (partitionBy === 'sku') {
      if (measurement.grain === 'observations') {
        return this.evaluateSkuObservations(measurement, nodeMap);
      }
      return this.evaluateSku(measurement, nodeMap);
    }
    if (partitionBy === 'brand') {
      if (measurement.grain === 'observations') {
        if (measurement.shareMode !== 'absolute') {
          return this.evaluateBrandShares(measurement, nodeMap, measurement.shareMode, options);
        }
        return this.evaluateBrandObservations(measurement, nodeMap, options);
      }
      if (measurement.show !== undefined) {
        return this.evaluateBrandSkuGrain(measurement, signal);
      }
      return this.evaluateBrandSeriesGrain();
    }
    // Drift guard: assertSupportedSlots rejects every other partitionBy before this point.
    throw new Error(
      `MeasurementEvaluatorService.evaluate: no route for partitionBy="${partitionBy}" — ` +
        `the dispatch and assertSupportedSlots have drifted apart`
    );
  }

  // ─── Private evaluation paths ────────────────────────────────────────

  private async evaluateNone(
    measurement: Measurement,
    nodeMap: CategoryNodeMap | null
  ): Promise<ScalarResult | SeriesResult> {
    const eans = await this.resolveEANs(measurement, nodeMap);
    const batch = await this.observationRepo.readBatch(eans);

    const allObs: SeriesObservation[] = [];
    for (const result of batch.values()) {
      for (const r of result.observations) {
        if (r.kind === 'ok') allObs.push(r.observation);
      }
    }
    const filtered = filterObservations(allObs, measurement.filter);
    const win = measurement.window!;

    if (win.mode === 'total') {
      return { kind: 'scalar', value: applyMeasure(measurement.measure, filtered) };
    }

    return { kind: 'series', buckets: computeBucketMap(filtered, measurement.measure, win) };
  }

  private async evaluateSku(
    measurement: Measurement,
    nodeMap: CategoryNodeMap | null
  ): Promise<PartitionedResult> {
    const eans = await this.resolveEANs(measurement, nodeMap);
    const batch = await this.observationRepo.readBatch(eans);
    const win = measurement.window!;

    const partitions = new Map<string, BucketMap>();
    for (const ean of eans) {
      const result = batch.get(ean);
      const obs: SeriesObservation[] = result
        ? result.observations
            .filter((r): r is { kind: 'ok'; observation: Observation } => r.kind === 'ok')
            .map((r) => r.observation)
        : [];
      const filtered = filterObservations(obs, measurement.filter);
      partitions.set(ean, computeBucketMap(filtered, measurement.measure, win));
    }
    return { kind: 'partitioned', partitions };
  }

  private async evaluateBrandSkuGrain(
    measurement: Measurement,
    signal: AbortSignal | undefined
  ): Promise<PartitionedResult> {
    const slugs = measurement.show!;
    const win = measurement.window!;
    const read = await this.observationRepo.readWholeBrandSeries(
      slugs,
      monthsForWindow(win),
      signal
    );

    const partitions = new Map<string, BucketMap>();
    for (const slug of slugs) {
      const filtered = filterObservations(decodeObservations(read.get(slug)), measurement.filter);
      partitions.set(slug, computeBucketMap(filtered, measurement.measure, win));
    }
    return { kind: 'partitioned', partitions };
  }

  private evaluateBrandSeriesGrain(): never {
    throw new Error(
      'MeasurementEvaluatorService: partitionBy:brand with grain:aggregated and show:absent is not implemented (D-03). ' +
        'Pass grain:observations for brand-partitioned raw series.'
    );
  }

  private async evaluateSkuObservations(
    measurement: Measurement,
    nodeMap: CategoryNodeMap | null
  ): Promise<PartitionedObservationsResult> {
    const eans = await this.resolveEANs(measurement, nodeMap);
    const win = measurement.window;
    const batch = await this.observationRepo.readBatch(
      eans,
      win ? monthsForWindow(win) : undefined
    );
    return this._buildPartitionedObservations(this._decodeBatch(eans, batch), measurement.filter);
  }

  // ACL boundary: ObservationRepository owns b-/bps-/bc- key construction; the evaluator never constructs those keys.
  private async evaluateBrandObservations(
    measurement: Measurement,
    nodeMap: CategoryNodeMap | null,
    options: EvaluateOptions
  ): Promise<PartitionedObservationsResult> {
    // Pass show as slugFilter so bc- and brand-in paths narrow at fetch time (perf).
    // A rollup also narrows post-fetch via slugFilter. The fallback path cannot narrow
    // at fetch time (brand attribution is unknown before reads), so narrowToShow handles it.
    const allObs = await this._fetchAllBrandObservations(
      measurement,
      nodeMap,
      measurement.show,
      options
    );
    const narrowed = measurement.show ? narrowToShow(allObs, measurement.show) : allObs;
    return this._buildPartitionedObservations(narrowed, measurement.filter);
  }

  // Computes per-bucket share fractions for brand+observations with a non-absolute shareMode.
  // ofUniverse/ofTotal fetch the full universe (slugFilter=undefined) to build the denominator.
  // ofSelection only needs shown brands (slugFilter=measurement.show) since denominator = shown sum.
  // Returns PartitionedResult with values in [0, 1].
  private async evaluateBrandShares(
    measurement: Measurement,
    nodeMap: CategoryNodeMap | null,
    shareMode: Exclude<ShareMode, 'absolute'>,
    options: EvaluateOptions
  ): Promise<PartitionedResult> {
    // ofSelection denominator is the shown brands only; no need to fetch the full universe.
    const slugFilter = shareMode === 'ofSelection' ? measurement.show : undefined;
    const allObs = await this._fetchAllBrandObservations(measurement, nodeMap, slugFilter, options);
    const win = measurement.window!;

    const allBrandBuckets = new Map<string, BucketMap>();
    for (const [slug, obs] of allObs) {
      const filtered = filterObservations(obs, measurement.filter);
      allBrandBuckets.set(slug, computeBucketMap(filtered, measurement.measure, win));
    }

    const denominator = computeShareDenominator(allBrandBuckets, measurement.show, shareMode, win);

    // Output only the shown brands (or all when show is absent).
    const outputSlugs = measurement.show
      ? [...allBrandBuckets.keys()].filter((s) =>
          (measurement.show as readonly string[]).includes(s)
        )
      : [...allBrandBuckets.keys()];

    const partitions = new Map<string, BucketMap>();
    for (const slug of outputSlugs) {
      const buckets = allBrandBuckets.get(slug);
      if (!buckets) continue;
      partitions.set(slug, applyShareDenominator(buckets, denominator));
    }

    return { kind: 'partitioned', partitions };
  }

  // Fetches raw brand observations.
  // slugFilter, when set, limits which slugs are fetched for bc- and brand-in paths (paths that
  // support per-slug selection). Pass measurement.show for absolute/ofSelection — those do not
  // need the full universe. Pass undefined for ofUniverse/ofTotal to include all universe brands.
  // The rollup path always returns it whole (readBrandSeries has no slug selection).
  private async _fetchAllBrandObservations(
    measurement: Measurement,
    nodeMap: CategoryNodeMap | null,
    slugFilter: readonly string[] | undefined,
    { signal, categoryMeta }: EvaluateOptions
  ): Promise<Map<string, readonly SeriesObservation[]>> {
    const { universe } = measurement;
    const win = measurement.window!;
    const monthsYM = monthsForWindow(win);
    const filterSet = slugFilter ? new Set(slugFilter) : null;

    // Rollup reference: the backend's precomputed stored-segment series (bps- path at the ACL
    // boundary). readBrandSeries has no per-slug API; returns it whole. Callers narrow if needed.
    if (universe.kind === 'rollup') {
      const brandPayloads = await this.observationRepo.readBrandSeries(
        String(universe.id),
        monthsYM,
        signal
      );
      if (filterSet) {
        return new Map([...brandPayloads].filter(([slug]) => filterSet.has(slug)));
      }
      return new Map(brandPayloads);
    }

    const { predicate } = universe;
    if (predicate !== false && predicate !== true) {
      const spec = predicate;

      // Brand + category composite: bc- rollup path (brand × category precomputed reads).
      const bcSpec = extractBrandCategorySpec(spec);
      if (bcSpec !== null) {
        if (nodeMap === null) {
          throw new Error(
            `MeasurementEvaluatorService.evaluate: nodeMap is required for ` +
              `brand+category composite universe (bc- path)`
          );
        }
        const slugsToFetch = filterSet
          ? bcSpec.slugs.filter((s) => filterSet.has(s))
          : bcSpec.slugs;
        // Read for the brands the handed metadata does not name, and only for those.
        const unnamed = slugsToFetch.filter((slug) => !categoryMeta?.has(slug));
        const readMeta =
          unnamed.length > 0
            ? await this.observationRepo.loadCategoryMetaOfBrands(unnamed, signal)
            : new Map<string, readonly BrandCategoryMeta[]>();
        const prefixes = nonEmptyOf([
          ...categoryPrefixesUnder(
            slugsToFetch.map((slug) => categoryMeta?.get(slug) ?? readMeta.get(slug) ?? []),
            bcSpec.nodeId,
            nodeMap
          ),
        ]);
        const read =
          prefixes === null
            ? new Map<string, CacheRowResult>()
            : await this.observationRepo.readBrandCategorySeries(
                slugsToFetch,
                prefixes,
                monthsYM,
                signal
              );
        return new Map(slugsToFetch.map((slug) => [slug, decodeObservations(read.get(slug))]));
      }

      // Brand-in universe: precomputed per-brand rollups (b-{slug} reads at ACL boundary).
      if (spec.kind === 'brand-in') {
        const slugsToFetch = filterSet ? spec.slugs.filter((s) => filterSet.has(s)) : spec.slugs;
        const read = await this.observationRepo.readWholeBrandSeries(
          slugsToFetch,
          monthsYM,
          signal
        );
        return new Map(slugsToFetch.map((slug) => [slug, decodeObservations(read.get(slug))]));
      }
    }

    // Fallback: any other inline universe — resolve EANs with brand attribution then read per-EAN.
    if (nodeMap === null) {
      throw new Error(
        `MeasurementEvaluatorService.evaluate: nodeMap is required for ` +
          `brand-observations fallback path (non-precomputed universe)`
      );
    }
    const eanBrandMap = await this.segmentEvaluator.evaluateWithBrand(predicate, nodeMap);
    const eans = [...eanBrandMap.keys()];
    const batch = await this.observationRepo.readBatch(eans, monthsYM);

    const byBrand = new Map<string, SeriesObservation[]>();
    for (const [ean, brandSlug] of eanBrandMap) {
      const obs = decodeObservations(batch.get(ean));
      if (!byBrand.has(brandSlug)) byBrand.set(brandSlug, []);
      byBrand.get(brandSlug)!.push(...obs);
    }
    return byBrand;
  }

  private _decodeBatch(
    keys: readonly EAN[],
    batch: CacheBatchResult
  ): Map<string, readonly SeriesObservation[]> {
    const decoded = new Map<string, readonly SeriesObservation[]>();
    for (const key of keys) {
      decoded.set(key, decodeObservations(batch.get(key)));
    }
    return decoded;
  }

  private _buildPartitionedObservations(
    decoded: ReadonlyMap<string, readonly SeriesObservation[]>,
    filter: MeasurementFilter
  ): PartitionedObservationsResult {
    const partitions = new Map<string, readonly SeriesObservation[]>();
    for (const [key, obs] of decoded) {
      partitions.set(key, filterObservations(obs, filter));
    }
    return { kind: 'partitioned-observations', partitions };
  }

  private async resolveEANs(
    measurement: Measurement,
    nodeMap: CategoryNodeMap | null
  ): Promise<readonly EAN[]> {
    if (nodeMap === null) {
      throw new Error(
        `MeasurementEvaluatorService.evaluate: nodeMap is required for ` +
          `partitionBy="${measurement.partitionBy}" (EAN resolution path)`
      );
    }
    const { universe } = measurement;
    if (universe.kind === 'rollup') {
      throw new Error(
        `MeasurementEvaluatorService.evaluate: a rollup universe (segment "${universe.id}") has no ` +
          `enumerable members; partitionBy="${measurement.partitionBy}" needs an inline universe`
      );
    }
    const eans = await this.segmentEvaluator.evaluate(universe.predicate, nodeMap);
    return [...eans];
  }
}

// ─── Module-private helpers ───────────────────────────────────────────────────

function decodeObservations(result: CacheRowResult | undefined): SeriesObservation[] {
  if (!result) return [];
  return result.observations
    .filter((r): r is DecodeSuccess => r.kind === 'ok')
    .map((r) => r.observation);
}

// The distinct raw categories the brands' metadata names under the node.
function categoryPrefixesUnder(
  metaPerBrand: readonly (readonly BrandCategoryMeta[])[],
  nodeId: string,
  nodeMap: CategoryNodeMap
): ReadonlySet<string> {
  const prefixes = new Set<string>();
  for (const rows of metaPerBrand) {
    for (const { category } of rows) {
      const id = nodeMap.medpdb2PathToId(category);
      if (id !== undefined && (id === nodeId || id.startsWith(`${nodeId}/`))) {
        prefixes.add(category);
      }
    }
  }
  return prefixes;
}

/** Returns only the entries whose key appears in the show list. */
function narrowToShow<V>(
  partitions: ReadonlyMap<string, V>,
  show: readonly string[]
): Map<string, V> {
  const showSet = new Set<string>(show);
  const result = new Map<string, V>();
  for (const [key, val] of partitions) {
    if (showSet.has(key)) result.set(key, val);
  }
  return result;
}

/**
 * If spec is all([brand-in(slugs), category-under(nodeId)]) with exactly those two
 * predicates, returns the extracted brand slugs and node id. Any other shape returns null
 * so the caller falls through to the EAN-resolution fallback.
 */
function extractBrandCategorySpec(
  spec: Exclude<SkuPredicate, boolean>
): { readonly slugs: readonly string[]; readonly nodeId: string } | null {
  if (spec.kind !== 'all') return null;
  let brandIn: { readonly slugs: readonly string[] } | null = null;
  let categoryUnder: { readonly nodeId: string } | null = null;
  let otherCount = 0;
  for (const p of spec.predicates) {
    if (p === true || p === false) {
      otherCount++;
      continue;
    }
    if (p.kind === 'brand-in') {
      brandIn = p;
      continue;
    }
    if (p.kind === 'category-under') {
      categoryUnder = p;
      continue;
    }
    otherCount++;
  }
  if (otherCount > 0 || brandIn === null || categoryUnder === null) return null;
  return { slugs: brandIn.slugs, nodeId: categoryUnder.nodeId };
}

/** EARS AC-02: throws with an actionable error naming all unsupported slot values. */
function assertSupportedSlots(m: Measurement): void {
  const { unsupported, frame } = routeSlots(m);
  if (unsupported.length === 0) return;

  throw new Error(
    `MeasurementEvaluatorService: unsupported slot combination${frame} — ${unsupported.join(', ')}. ` +
      `Only the consumed inventory is implemented (D-03); extend the evaluator when adding ` +
      `a new surface.`
  );
}

/**
 * The inventory a measurement is graded against, and how the message frames it.
 *
 * The two single-combination families are scoped to the slot that selects them — distribution
 * by its measure, the map by its partition — so that slot frames the message and the list
 * holds only what deviates from that one combination. The observation families own everything
 * else and list the measure itself when the measure is what is unsupported.
 *
 * Order matters: the measure picks the fact family before the partition does, the same way
 * evaluate() dispatches, so measure:distribution keeps its own inventory at every partition.
 */
function routeSlots(m: Measurement): { readonly unsupported: string[]; readonly frame: string } {
  if (m.measure === 'distribution') {
    return { unsupported: distributionSlots(m), frame: ' for measure:distribution' };
  }
  if (m.partitionBy === 'region') {
    return { unsupported: regionSlots(m), frame: ' for partitionBy:region' };
  }
  if (m.partitionBy === 'vendor') {
    return { unsupported: vendorSlots(m), frame: ' for partitionBy:vendor' };
  }
  return { unsupported: observationSlots(m), frame: '' };
}

/**
 * The slots the observation-series families support (every measure except distribution).
 *
 * partitionBy:region and partitionBy:vendor are absent: neither reaches here, and the map
 * family owns both inventories (regionSlots, vendorSlots).
 */
function observationSlots(m: Measurement): string[] {
  const unsupported: string[] = [];

  if (m.partitionBy === 'channel' || m.partitionBy === 'segment') {
    unsupported.push(`partitionBy:${m.partitionBy}`);
  }

  if (m.window?.mode === 'weekly') {
    unsupported.push('window.mode:weekly');
  }

  if (m.compare !== undefined) {
    unsupported.push('compare:present');
  }

  // Non-absolute shareMode is accepted only for brand+observations (real consumer, unit 08).
  if (m.shareMode !== 'absolute' && !(m.partitionBy === 'brand' && m.grain === 'observations')) {
    unsupported.push(`shareMode:${m.shareMode}`);
  }

  // distribution is absent: it never reaches here, distributionSlots owns its inventory.
  const unsupportedMeasures = new Set<string>([
    'lostSales',
    'marketShare',
    'price',
    'listPrice',
    'availability',
  ]);
  if (unsupportedMeasures.has(m.measure)) {
    unsupported.push(`measure:${m.measure}`);
  }

  if (m.show !== undefined && m.partitionBy !== 'brand') {
    unsupported.push(`show:present with partitionBy:${m.partitionBy}`);
  }

  // A region grain describes the region partition's keys and nothing else, so any other
  // partition carrying one would silently drop an input the caller meant to be honoured.
  if (m.regionGrain !== undefined) {
    unsupported.push(`regionGrain:present with partitionBy:${m.partitionBy}`);
  }

  // The post-read region narrowing cuts regional facts, which carry a region per cell. An
  // observation series carries no region, so honouring it is impossible here and ignoring it
  // would answer over more facts than were asked for.
  if (m.filter.regions !== undefined) unsupported.push('filter.regions:present');

  // grain:observations is valid with partitionBy:sku and partitionBy:brand only
  if (m.grain === 'observations' && m.partitionBy !== 'sku' && m.partitionBy !== 'brand') {
    unsupported.push(`grain:observations with partitionBy:${m.partitionBy}`);
  }

  return unsupported;
}

/** Applies the measure catalog function to a flat observation list. */
function applyMeasure(measureId: MeasureId, obs: readonly SeriesObservation[]): number | null {
  switch (measureId) {
    case 'volume':
      return sumVolume(obs);
    case 'value':
      return sumValue(obs);
    case 'asp':
      return computeAsp(obs);
    default:
      // All remaining measure ids are in assertSupportedSlots' unsupported set,
      // so this branch is only reachable if the dispatch table is extended
      // without adding the measure to assertSupportedSlots — flag it loudly.
      throw new Error(
        `MeasurementEvaluatorService: measure "${measureId}" reached the evaluator ` +
          `dispatch but has no catalog wiring. Add it to applyMeasure() when the ` +
          `catalog function lands (e.g. lostSales is unit 08).`
      );
  }
}

/** Builds a BucketMap from obs under the given window. */
function computeBucketMap(
  obs: readonly SeriesObservation[],
  measureId: MeasureId,
  win: MeasurementWindow
): BucketMap {
  const keys = bucketKeys(win);
  const byBucket = assignObsToBuckets(obs, win);
  const out = new Map<string, number | null>();
  for (const k of keys) {
    const bucketObs = byBucket.get(k) ?? [];
    out.set(k, applyMeasure(measureId, bucketObs));
  }
  return out;
}

/**
 * Computes the per-bucket denominator for a share mode.
 *
 * ofUniverse: sum across ALL universe brands (full pool).
 * ofTotal:    EAN→brand immutability (CONTEXT.md §Brand) makes brands MECE over SKUs, so
 *             sum(brand volumes) = distinct-SKU total; ofTotal therefore uses the same
 *             universe-sum as ofUniverse. This equivalence is load-bearing: do not silently
 *             change one without re-checking the other.
 * ofSelection: sum across SHOWN brands only (measurement.show).
 *
 * For a MECE brand partition with ofUniverse/ofTotal, all displayed shares sum to 1.0
 * within rounding (PRD AC-05).
 */
function computeShareDenominator(
  allBrandBuckets: ReadonlyMap<string, BucketMap>,
  show: readonly string[] | undefined,
  shareMode: Exclude<ShareMode, 'absolute'>,
  win: MeasurementWindow
): BucketMap {
  const showSet = show ? new Set(show) : null;
  const denomSlugs =
    shareMode === 'ofSelection' && showSet
      ? [...allBrandBuckets.keys()].filter((s) => showSet.has(s))
      : [...allBrandBuckets.keys()];

  const keys = bucketKeys(win);
  const out = new Map<string, number | null>();
  for (const k of keys) {
    let total: number | null = null;
    for (const slug of denomSlugs) {
      const val = allBrandBuckets.get(slug)?.get(k);
      if (val !== null && val !== undefined) {
        total = (total ?? 0) + val;
      }
    }
    out.set(k, total);
  }
  return out;
}

/**
 * Divides each bucket value by the corresponding denominator value.
 * - null: value is null (no data for that bucket), denominator is null (no
 *   universe data), or denominator is 0 (universe bucket has zero volume;
 *   share is undefined).
 * - Throws if a bucket key is absent from the denominator map — both maps are
 *   keyed by bucketKeys(win), so a missing key is a programming error.
 */
function applyShareDenominator(buckets: BucketMap, denominator: BucketMap): BucketMap {
  const out = new Map<string, number | null>();
  for (const [k, v] of buckets) {
    const denom = denominator.get(k);
    if (denom === undefined) {
      throw new Error(`applyShareDenominator: bucket key '${k}' missing from denominator map`);
    }
    if (v === null || denom === null || denom === 0) {
      out.set(k, null);
    } else {
      out.set(k, v / denom);
    }
  }
  return out;
}

/**
 * Derives the YYYY-MM month strings that must be fetched for a window,
 * including the rolling-28 buffer range (27 days before range.from).
 */
function monthsForWindow(win: MeasurementWindow): NonEmpty<string> {
  // Measurement() refuses a range that ends before it starts, so the helper never throws here.
  const range = observationSourceRange(win);
  return monthsForDayRange(range.from, range.to);
}
